import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, Pagination, Select, StatusBadge, useLocale, type DataTableColumn } from '@ministry/ui/internal';

import { listAvailability, updateUserAvailability, AVAILABILITY_STATUSES, type StaffAvailabilityRow } from '../lib/availability.js';
import { availabilityStatusEntry } from '../lib/appointment-lifecycle.js';
import { ApiError } from '../lib/api-client.js';
import { useT } from '../i18n/strings.js';
import { PageHeader } from '../components/PageHeader.js';
import { ForbiddenState } from '../components/ForbiddenState.js';

/**
 * SCREEN_SPECS.md I13. HR-level bulk view + inline override. `GET /availability` returns
 * every staff member's row in one page-sized call (7 seeded staff; the endpoint IS
 * offset-paginated per `ListAvailabilityParams`, so this scales past one page the same way
 * File Register does), reusing I12's own status vocabulary for the inline edit rather than
 * inventing a second one.
 */
export function AvailabilityAdminPage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const queryClient = useQueryClient();
  const [page, setPage] = React.useState(1);
  const [pendingEdits, setPendingEdits] = React.useState<Record<string, { statusCode: string; error?: string }>>({});

  const query = useQuery({
    queryKey: ['availability-admin', page],
    queryFn: ({ signal }) => listAvailability({ page, pageSize: 25 }, signal),
  });

  const mutation = useMutation({
    mutationFn: (input: { userId: string; statusCode: string; expectedVersion: number }) =>
      updateUserAvailability(input.userId, { statusCode: input.statusCode, expectedVersion: input.expectedVersion }),
    onSuccess: (updated, variables) => {
      setPendingEdits((prev) => {
        const next = { ...prev };
        delete next[variables.userId];
        return next;
      });
      queryClient.setQueryData<{ rows: StaffAvailabilityRow[]; page: { page: number; pageSize: number; totalItems: number; totalPages: number } } | undefined>(
        ['availability-admin', page],
        (prev) =>
          prev
            ? {
                ...prev,
                rows: prev.rows.map((row) =>
                  row.userId === variables.userId ? { ...row, status: updated.status, internalNote: updated.internalNote, version: updated.version } : row,
                ),
              }
            : prev,
      );
    },
    onError: (error, variables) => {
      const message =
        error instanceof ApiError && error.code === 'CONFLICT_HOLDER_CHANGED'
          ? t('availability.admin.error.conflict')
          : t('availability.admin.error.generic');
      setPendingEdits((prev) => ({ ...prev, [variables.userId]: { statusCode: variables.statusCode, error: message } }));
      // A stale conflict means this row's `version` is wrong — refetch so the next attempt uses the real current value.
      if (error instanceof ApiError && error.code === 'CONFLICT_HOLDER_CHANGED') {
        void query.refetch();
      }
    },
  });

  if (query.isError && query.error instanceof ApiError && query.error.code.startsWith('FORBIDDEN')) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('availability.admin.title')} />
        <ForbiddenState error={query.error} />
      </div>
    );
  }

  const rows = query.data?.rows ?? [];

  const columns: DataTableColumn<StaffAvailabilityRow>[] = [
    {
      key: 'fullName',
      header: t('availability.admin.column.name'),
      visibleFrom: 'always',
      cardSlot: 'title',
      grow: true,
      render: (row) => row.fullName,
    },
    {
      key: 'designation',
      header: t('availability.admin.column.designation'),
      visibleFrom: 'md',
      cardSlot: 'line2',
      render: (row) =>
        row.designation === null ? t('common.notSet') : locale === 'ne' ? row.designation.labelNe : row.designation.labelEn,
    },
    {
      key: 'orgUnit',
      header: t('availability.admin.column.orgUnit'),
      visibleFrom: 'md',
      cardSlot: 'line3',
      render: (row) =>
        row.orgUnit === null ? t('common.notSet') : locale === 'ne' ? row.orgUnit.nameNe : row.orgUnit.nameEn,
    },
    {
      key: 'status',
      header: t('availability.admin.column.status'),
      visibleFrom: 'always',
      cardSlot: 'status',
      render: (row) => <StatusBadge entry={availabilityStatusEntry(row.status.code, row.status.labelNe, row.status.labelEn)} />,
    },
    {
      key: 'override',
      header: t('availability.admin.column.override'),
      visibleFrom: 'always',
      cardSlot: 'line4',
      render: (row) => {
        const pending = pendingEdits[row.userId];
        const value = pending?.statusCode ?? row.status.code;
        return (
          <div className="flex min-w-[10rem] flex-col gap-1">
            <Select
              id={`availability-admin-status-${row.userId}`}
              label={t('availability.admin.column.override')}
              hideLabel
              options={AVAILABILITY_STATUSES.map((s) => ({ value: s.code, label: locale === 'ne' ? s.labelNe : s.labelEn }))}
              value={value}
              onValueChange={(nextCode) => {
                setPendingEdits((prev) => ({ ...prev, [row.userId]: { statusCode: nextCode } }));
                mutation.mutate({ userId: row.userId, statusCode: nextCode, expectedVersion: row.version });
              }}
              disabled={mutation.isPending && mutation.variables?.userId === row.userId}
            />
            {pending?.error ? <span className="text-xs text-status-critical-tint-fg">{pending.error}</span> : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('availability.admin.title')} description={t('availability.admin.description')} />

      <DataTable
        caption={t('availability.admin.caption')}
        columns={columns}
        rows={rows}
        getRowId={(row) => row.userId}
        loading={query.isLoading}
        error={query.isError}
        onRetry={() => query.refetch()}
        emptyTitle={t('availability.admin.empty.title')}
        emptyBody={t('availability.admin.empty.body')}
      />

      {query.data && query.data.page.totalPages > 1 ? (
        <Pagination page={page} pageCount={query.data.page.totalPages} onPageChange={setPage} loading={query.isFetching} />
      ) : null}
    </div>
  );
}
